import React, { useEffect } from 'react';
import { Navigate, Route, Routes } from 'react-router-dom';
import { useSelector } from 'react-redux';
import AuthChoicePage from './pagesv2/AuthChoicePage';
import LoginPage from './pagesv2/LoginPage';
import PartnerDashboard from './pagesv2/PartnerDashboard';
import SuperAdminDashboard from './pagesv2/SuperAdminDashboard';
import AppInteractionGuard from './components/AppInteractionGuard';
import GlobalLoadingOverlay from './components/GlobalLoadingOverlay';
import ThemeToggle from './components/ThemeToggle';
import ResponsiveTableLabels from './components/ResponsiveTableLabels';
import { ThemeProvider, useTheme } from './theme';

// Use a versioned key so existing installations receive the redesigned light
// workspace instead of retaining a legacy dark-theme preference.
const THEME_STORAGE_KEY = 'barbellos-theme-v2';

function getInitialTheme() {
  const savedTheme = localStorage.getItem(THEME_STORAGE_KEY);
  if (savedTheme === 'light' || savedTheme === 'dark') {
    return savedTheme;
  }
  // The workspace is designed as a light enterprise application by default.
  // Users can still explicitly choose dark mode from the theme toggle.
  return 'light';
}

function useResponsiveIconButtons() {
  useEffect(() => {
    const iconSelectors = ['svg', '.btn__icon', '.MuiSvgIcon-root', '.material-icons', '.fa', '[class*="icon"]'];

    const hasIcon = (element) => iconSelectors.some((selector) => element.querySelector(selector));
    const hasVisibleText = (element) => {
      return Array.from(element.childNodes).some((node) =>
        node.nodeType === Node.TEXT_NODE && node.textContent.trim().length > 0
      );
    };

    const updateButtons = () => {
      const isSmall = window.matchMedia('(max-width: 768px)').matches;
      document.querySelectorAll('button, a, [role="button"]').forEach((element) => {
        if (!(element instanceof HTMLElement)) {
          return;
        }

        const containsIcon = hasIcon(element);
        const containsText = hasVisibleText(element);

        if (containsIcon && containsText && isSmall) {
          element.classList.add('responsive-icon-only');
        } else {
          element.classList.remove('responsive-icon-only');
        }
      });
    };

    updateButtons();
    const observer = new MutationObserver(updateButtons);
    observer.observe(document.body, { childList: true, subtree: true });
    window.addEventListener('resize', updateButtons);
    return () => {
      observer.disconnect();
      window.removeEventListener('resize', updateButtons);
    };
  }, []);
}

function hasRole(roles = [], targetRole) {
  const normalized = roles.map((role) => String(role).toLowerCase());
  if (targetRole === 'partner') {
    return normalized.includes('partner');
  }
  if (targetRole === 'superadmin') {
    return normalized.includes('superadmin') || normalized.includes('super_admin') || normalized.includes('admin');
  }
  return false;
}

function ProtectedRoute({ role, children }) {
  const token = useSelector((state) => state.auth.access_token);
  const roles = useSelector((state) => state.auth.roles);
  if (!token) {
    return <Navigate to="/login" replace />;
  }
  if (!hasRole(roles, role)) {
    return <Navigate to="/" replace />;
  }
  return children;
}

function App() {
  return (
    <ThemeProvider defaultTheme={getInitialTheme()}>
      <AppContent />
    </ThemeProvider>
  );
}

function AppContent() {
  useResponsiveIconButtons();

  const { theme, toggleTheme } = useTheme();

  return (
    <>
      <AppInteractionGuard />
      <GlobalLoadingOverlay />
      <ResponsiveTableLabels />
      <ThemeToggle theme={theme} onToggle={toggleTheme} />
      <Routes>
        <Route path="/" element={<AuthChoicePage />} />
        <Route path="/login" element={<LoginPage />} />
        <Route path="/login/:role" element={<Navigate to="/login" replace />} />
        <Route
          path="/partners-workspace"
          element={
            <ProtectedRoute role="partner">
              <PartnerDashboard />
            </ProtectedRoute>
          }
        />
        <Route
          path="/sa-workspace"
          element={
            <ProtectedRoute role="superadmin">
              <SuperAdminDashboard />
            </ProtectedRoute>
          }
        />
        <Route path="/partner" element={<Navigate to="/partners-workspace" replace />} />
        <Route path="/superadmin" element={<Navigate to="/sa-workspace" replace />} />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </>
  );
}

export default App;
